import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Center, HStack, Heading, Image, SimpleGrid, Text, useColorModeValue, useToast, VStack, Badge } from '@chakra-ui/react';
import AdminLayout from '@/layouts/AdminLayout';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAdminScoreboard, updateAdminScoreboard, ScoreboardState, startTimer, pauseTimer, resetTimer, startSecondHalf } from '@/services/scoreboard';

const MobileScoreboardControlPage: React.FC = () => {
  const toast = useToast();
  const qc = useQueryClient();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderCol = useColorModeValue('gray.200', 'gray.700');

  const { data: state, isLoading } = useQuery<ScoreboardState>({
    queryKey: ['admin-scoreboard-mobile'],
    queryFn: getAdminScoreboard,
    refetchInterval: (data: any) => (data?.running ? 1000 : false),
    staleTime: 500,
  });

  const setPartial = async (patch: Partial<ScoreboardState>) => {
    try {
      await updateAdminScoreboard(patch);
      await qc.invalidateQueries({ queryKey: ['admin-scoreboard-mobile'] });
    } catch (e) {
      toast({ title: 'Uložení selhalo', status: 'error' });
    }
  };

  // Use backend timer; control via API and reflect state via polling
  const handleStartTimer = async () => {
    await startTimer();
    await qc.invalidateQueries({ queryKey: ['admin-scoreboard-mobile'] });
  };

  const handlePauseTimer = async () => {
    await pauseTimer();
    await qc.invalidateQueries({ queryKey: ['admin-scoreboard-mobile'] });
  };

  const handleResetTimer = async () => {
    await resetTimer();
    await qc.invalidateQueries({ queryKey: ['admin-scoreboard-mobile'] });
  };

  const mmss = useMemo(() => {
    return state?.timer || '00:00';
  }, [state?.timer]);

  if (isLoading || !state) {
    return (
      <AdminLayout requireAdmin={false}>
        <Center minH="50vh">Načítání…</Center>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout requireAdmin={false}>
      <Box p={3}>
        <Heading size="md" mb={3}>Mobilní ovládání tabule</Heading>
        <VStack align="stretch" spacing={3}>
          <Box borderWidth="1px" borderColor={borderCol} bg={cardBg} borderRadius="lg" p={{ base: 3, md: 4 }}>
            <VStack spacing={3} align="stretch">
              <HStack justify="space-between" align="center" flexWrap="wrap">
                <Text fontSize={{ base: '4xl', md: '5xl' }} fontWeight="black" lineHeight="1">{state.homeScore} : {state.awayScore}</Text>
                <Text fontSize={{ base: '3xl', md: '4xl' }} fontFamily="mono" fontWeight="semibold">{mmss}</Text>
              </HStack>
              <HStack spacing={2} wrap="wrap">
                <Button size="lg" colorScheme={state.running ? 'red' : 'green'} onClick={() => (state.running ? handlePauseTimer() : handleStartTimer())}>
                  {state.running ? 'Stop' : 'Start'}
                </Button>
                <Button size="lg" variant="outline" onClick={handleResetTimer}>Reset</Button>
                <Button size="lg" colorScheme="purple" onClick={async ()=>{ try { await startSecondHalf(); await qc.invalidateQueries({ queryKey: ['admin-scoreboard-mobile'] }); toast({ title: 'Začal 2. poločas', status: 'success' }); } catch { toast({ title: 'Akce selhala', status: 'error' }); } }}>
                  Začít 2. poločas
                </Button>
                <Badge ml="auto" colorScheme="purple" fontSize={{ base: 'sm', md: 'md' }}>Poločas: {state.half || 1}</Badge>
              </HStack>
            </VStack>
          </Box>

          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} alignItems="stretch">
            <VStack spacing={3} borderWidth="1px" borderColor={borderCol} bg={cardBg} borderRadius="lg" p={{ base: 3, md: 4 }} align="stretch">
              {state.homeLogo ? <Image src={state.homeLogo} alt="DOM" boxSize={{ base: '56px', md: '64px' }} objectFit="contain" alignSelf="center" /> : null}
              <Text fontWeight="bold" textAlign="center">{state.homeShort || 'DOM'}</Text>
              <HStack justify="center">
                <Button size="lg" onClick={() => setPartial({ homeScore: Math.max(0, (state.homeScore || 0) - 1) })}>−</Button>
                <Button size="lg" colorScheme="green" onClick={() => setPartial({ homeScore: (state.homeScore || 0) + 1 })}>+</Button>
              </HStack>
              <HStack justify="center">
                <Button size="sm" onClick={() => setPartial({ homeFouls: Math.max(0, Math.min(5, (state.homeFouls || 0) - 1)) })}>− Faul</Button>
                <Text fontWeight="semibold">{Math.max(0, Math.min(5, state.homeFouls || 0))}</Text>
                <Button size="sm" colorScheme="orange" onClick={() => setPartial({ homeFouls: Math.max(0, Math.min(5, (state.homeFouls || 0) + 1)) })}>+ Faul</Button>
              </HStack>
            </VStack>

            <VStack spacing={3} borderWidth="1px" borderColor={borderCol} bg={cardBg} borderRadius="lg" p={{ base: 3, md: 4 }} align="stretch">
              {state.awayLogo ? <Image src={state.awayLogo} alt="HOS" boxSize={{ base: '56px', md: '64px' }} objectFit="contain" alignSelf="center" /> : null}
              <Text fontWeight="bold" textAlign="center">{state.awayShort || 'HOS'}</Text>
              <HStack justify="center">
                <Button size="lg" onClick={() => setPartial({ awayScore: Math.max(0, (state.awayScore || 0) - 1) })}>−</Button>
                <Button size="lg" colorScheme="green" onClick={() => setPartial({ awayScore: (state.awayScore || 0) + 1 })}>+</Button>
              </HStack>
              <HStack justify="center">
                <Button size="sm" onClick={() => setPartial({ awayFouls: Math.max(0, Math.min(5, (state.awayFouls || 0) - 1)) })}>− Faul</Button>
                <Text fontWeight="semibold">{Math.max(0, Math.min(5, state.awayFouls || 0))}</Text>
                <Button size="sm" colorScheme="orange" onClick={() => setPartial({ awayFouls: Math.max(0, Math.min(5, (state.awayFouls || 0) + 1)) })}>+ Faul</Button>
              </HStack>
            </VStack>
          </SimpleGrid>
        </VStack>

        {/* Removed 'Vybraný zápas' section for remote – managed on main Tabule page */}
      </Box>
    </AdminLayout>
  );
};

export default MobileScoreboardControlPage;
